import { Controller } from "@hotwired/stimulus"

const SORT_STATE = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
  NONE: "none",
}

const NEXT_SORT_STATE = {
  [SORT_STATE.ASCENDING]: SORT_STATE.DESCENDING,
  [SORT_STATE.DESCENDING]: SORT_STATE.NONE,
  [SORT_STATE.NONE]: SORT_STATE.ASCENDING,
}

const SORT_FOCUS_OFFSET = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
}

// The header cell's aria-sort carries the state, so the button is named for the
// action alone. Repeating the state here makes screen readers say it twice.
function sortButtonLabel(columnLabel) {
  return `Sort by ${columnLabel}`
}

function sortAnnouncement(columnLabel, sortState) {
  const templates = {
    [SORT_STATE.ASCENDING]: `${columnLabel}, sorted ascending`,
    [SORT_STATE.DESCENDING]: `${columnLabel}, sorted descending`,
    [SORT_STATE.NONE]: `${columnLabel}, sort cleared`,
  }

  return templates[sortState] || templates[SORT_STATE.NONE]
}

function parseSortableValue(value) {
  const percentMatch = value.match(/^(\d+(?:\.\d+)?)\s*%$/)
  if (percentMatch) return parseFloat(percentMatch[1])

  const normalized = value.replace(/,/g, "")
  if (/^-?\d+(\.\d+)?$/.test(normalized)) return parseFloat(normalized)

  return value.toLowerCase()
}

function compareSortableValues(left, right) {
  const leftParsed = parseSortableValue(left)
  const rightParsed = parseSortableValue(right)

  if (typeof leftParsed === "number" && typeof rightParsed === "number") {
    return leftParsed - rightParsed
  }

  return String(leftParsed).localeCompare(String(rightParsed), undefined, {
    numeric: true,
    sensitivity: "base",
  })
}

export default class extends Controller {
  static targets = ["sortButton", "liveRegion"]

  connect() {
    this.tbodyElement = this.element.querySelector("tbody")
    this.originalRowOrder = null
    this.snapshotOriginalRows()
    this.syncSortButtonLabels()
  }

  disconnect() {
    this.tbodyElement = null
    this.originalRowOrder = null
  }

  toggle(event) {
    const button = event.currentTarget
    const headerCell = button.closest("th")
    if (!headerCell || !this.tbody) return

    this.clearOtherSortStates(headerCell)

    const nextState = this.nextSortState(headerCell.getAttribute("aria-sort"))
    headerCell.setAttribute("aria-sort", nextState)

    if (nextState === SORT_STATE.NONE) {
      this.restoreOriginalRows()
    } else {
      this.sortByColumn(headerCell, nextState === SORT_STATE.ASCENDING)
    }

    this.syncSortButtonLabels()
    this.announceSort(button, nextState)
  }

  sortKeydown(event) {
    if (!this.hasSortButtonTarget) return

    const buttons = this.sortButtonTargets
    const currentIndex = buttons.indexOf(event.currentTarget)
    if (currentIndex < 0) return

    let nextIndex

    if (event.key in SORT_FOCUS_OFFSET) {
      const offset = SORT_FOCUS_OFFSET[event.key]
      nextIndex = (currentIndex + offset + buttons.length) % buttons.length
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = buttons.length - 1
    } else {
      return
    }

    event.preventDefault()
    buttons[nextIndex].focus()
  }

  syncSortButtonLabels() {
    if (!this.hasSortButtonTarget) return

    this.sortButtonTargets.forEach((button) => {
      const columnLabel = button.dataset.columnLabel
      if (!columnLabel) return

      button.setAttribute("aria-label", sortButtonLabel(columnLabel))
    })
  }

  announceSort(button, sortState) {
    if (!this.hasLiveRegionTarget) return

    const columnLabel = button.dataset.columnLabel
    if (!columnLabel) return

    this.liveRegionTarget.textContent = sortAnnouncement(columnLabel, sortState)
  }

  get tbody() {
    return this.tbodyElement
  }

  snapshotOriginalRows() {
    if (!this.tbody || this.originalRowOrder) return

    this.originalRowOrder = Array.from(this.tbody.querySelectorAll("tr"))
  }

  restoreOriginalRows() {
    if (!this.tbody || !this.originalRowOrder) return

    this.originalRowOrder.forEach((row) => this.tbody.appendChild(row))
  }

  sortByColumn(headerCell, ascending) {
    const columnIndex = this.columnIndex(headerCell)
    if (columnIndex < 0 || !this.tbody) return

    const valueForRow = (row) => this.cellSortValue(row, columnIndex)

    Array.from(this.tbody.querySelectorAll("tr"))
      .sort((rowA, rowB) => {
        const comparison = compareSortableValues(valueForRow(rowA), valueForRow(rowB))
        return ascending ? comparison : -comparison
      })
      .forEach((row) => this.tbody.appendChild(row))
  }

  columnIndex(headerCell) {
    const headers = headerCell.closest("tr")?.querySelectorAll("th")
    if (!headers) return -1

    return Array.from(headers).indexOf(headerCell)
  }

  cellSortValue(row, columnIndex) {
    const cell = row.cells[columnIndex]
    if (!cell) return ""

    const sortValue = cell.dataset.sortValue?.trim()
    if (sortValue) return sortValue

    return cell.textContent?.trim() ?? ""
  }

  clearOtherSortStates(activeCell) {
    activeCell
      .closest("tr")
      ?.querySelectorAll("th[aria-sort]")
      .forEach((cell) => {
        if (cell !== activeCell) cell.setAttribute("aria-sort", SORT_STATE.NONE)
      })
  }

  nextSortState(current) {
    return NEXT_SORT_STATE[current] || SORT_STATE.ASCENDING
  }
}
